/**
 * Controller for UI functionality
 */
import { CONFIG } from '../config/config.js';
import { AppState } from './appState.js';
import { formatPopulation } from '../utils/formatter.js';
import { findNearestDataSigma as findNearestSigma } from '../utils/sigmaUtils.js';

export class UIController {
  constructor(mapController) {
    this.mapController = mapController;
    
    // Ensure DOM is fully loaded before setting up UI
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.setupUIElements();
        this.setupEventHandlers();
      });
    } else {
      this.setupUIElements();
      this.setupEventHandlers();
    }
  }
  
  /**
   * Set up UI elements
   */
  setupUIElements() {
    this.setupSigmaControls();
    this.initializeSliders();
    this.setupTimeSlider();
    
    // Setup collapsible panels last to ensure all DOM elements are ready
    this.setupCollapsiblePanels();
  }
  
  /**
   * Create sigma value controls
   */
  setupSigmaControls() {
    const hiddenControlsContainer = document.getElementById('hidden-sigma-controls');
    
    if (hiddenControlsContainer) {
      // Clear existing controls
      hiddenControlsContainer.innerHTML = '';
      
      // Create controls for each sigma value
      CONFIG.sigmaValues.forEach(sigma => {
        const sigmaControl = document.createElement('div');
        sigmaControl.innerHTML = `
          <div class="sigma-label-container">
            <input type="checkbox" id="sigma-${sigma}-toggle" checked>
            <span id="opacity-value-${sigma}">0%</span>
          </div>
          <input type="range" id="opacity-${sigma}" min="0" max="100" value="0">
        `;
        hiddenControlsContainer.appendChild(sigmaControl);
      });
    }
  }
  
  /**
   * Initialize slider values
   */
  initializeSliders() {
    CONFIG.sigmaValues.forEach(sigma => {
      const sigmaToggle = document.getElementById(`year-${sigma}-toggle`);
      const sigmaSlider = document.getElementById(`opacity-${sigma}`);
      
      if (sigmaToggle && sigmaSlider) {
        // Disable slider if toggle is unchecked
        sigmaSlider.disabled = !sigmaToggle.checked;
        
        // Set default values
        if (sigma === CONFIG.defaultSigma && sigmaToggle.checked) {
          sigmaSlider.value = 100;
          document.getElementById(`opacity-value-${sigma}`).textContent = '100%';
        } else if (sigmaToggle.checked) {
          sigmaSlider.value = 0;
          document.getElementById(`opacity-value-${sigma}`).textContent = '0%';
        }
      }
    });
  }
  
  /**
   * Set up collapsible panels
   */
  setupCollapsiblePanels() {
    const collapsibleHeaders = document.querySelectorAll('.collapsible-header');

    // First remove any existing listeners to avoid duplicates
    collapsibleHeaders.forEach(header => {
      const newHeader = header.cloneNode(true);
      header.parentNode.replaceChild(newHeader, header);
    });

    // Now add fresh event listeners
    document.querySelectorAll('.collapsible-header').forEach(header => {
      // Skip population header as it's handled separately
      if (header.id === 'poi-header') return;

      header.addEventListener('click', function() {
        // Toggle the active class on the header
        this.classList.toggle('active');

        // Get the content panel that follows this header
        const content = this.nextElementSibling;

        // Toggle visibility of the content
        if (content.style.maxHeight) {
          content.style.maxHeight = null;
        } else {
          content.style.maxHeight = content.scrollHeight + "px";
        }
      });
    });

    // Initialize panel states based on AppState.expandedPanels
    Object.entries(AppState.expandedPanels).forEach(([panelKey, isExpanded]) => {
      let headerId;
      // Map panelKey to the corresponding header ID
      if (panelKey === 'yearSlider') headerId = 'year-slider-header';
      else if (panelKey === 'watershedLayers') headerId = 'watershed-layers-header';
      else if (panelKey === 'population') headerId = 'poi-header'; // Use poi-header
      else if (panelKey === 'referenceLayers') headerId = 'reference-layers-header';
      else if (panelKey === 'basemaps') headerId = 'basemaps-header';

      if (headerId) {
        const header = document.getElementById(headerId);
        const content = header?.nextElementSibling;

        if (header && content && isExpanded) {
          // Special handling for population/POI panel
          if (headerId === 'poi-header') {
             const poiContent = document.getElementById('poi-content');
             if (poiContent) {
               header.classList.add('active');
               poiContent.classList.add('expanded');
             }
          } else {
            // Standard handling for other panels
            header.classList.add('active');
            setTimeout(() => {
                 content.style.maxHeight = content.scrollHeight + "px";
            }, 0);
          }
        } else if (header && !isExpanded) {
           header.classList.remove('active');
           if (content && headerId !== 'poi-header') {
               content.style.maxHeight = null;
           } else if (headerId === 'poi-header') {
               const poiContent = document.getElementById('poi-content');
               if (poiContent) poiContent.classList.remove('expanded');
           }
        }
      }
    });
  }
  
  /**
   * Set up time slider controls
   */
  setupTimeSlider() {
    const sigmaSlider = document.getElementById('sigma-slider'); // Corrected ID
    const sigmaDisplay = document.getElementById('sigma-display'); // Corrected ID
    
    // Enable all layers' checkboxes for the time slider functionality
    CONFIG.sigmaValues.forEach(sigma => {
      const toggle = document.getElementById(`sigma-${sigma}-toggle`);
      if (toggle) {
        toggle.checked = true;
        
        // Make sure sliders are enabled for all layers
        const slider = document.getElementById(`opacity-${sigma}`);
        if (slider) {
          slider.disabled = false;
        }
      }
    });
    
    // Initialize with the current slider value
    if (sigmaSlider) { // Add null check
      AppState.sigmaValue = parseFloat(sigmaSlider.value);
      this.updateLayersBySigma(AppState.sigmaValue);
    } else {
      console.error("Sigma slider element not found for setupTimeSlider");
    }
  }
  
  /**
   * Set up all event handlers
   */
  setupEventHandlers() {
    // Basemap handlers
    this.setupBasemapHandlers();
    
    // Layer type handlers
    this.setupLayerTypeHandlers();
    
    // Sigma toggle handlers
    this.setupSigmaToggleHandlers();
    
    // POI slider handlers
    this.setupPoiHandler();
    
    // Time slider handlers
    this.setupTimeSliderHandlers();
    
    // Cell size selector handler
    this.setupCellSizeHandler();
    
    // Preset selector handler
    this.setupPresetHandler();
    
    // Location search handlers
    this.setupLocationSearchHandlers();
    
    // Direct handler for POI header
    const poiHeader = document.getElementById('poi-header');
    const poiContent = document.getElementById('poi-content');
    
    if (poiHeader && poiContent) {
      // Remove previous listeners by cloning and replacing
      const newHeader = poiHeader.cloneNode(true);
      poiHeader.parentNode.replaceChild(newHeader, poiHeader);
      
      // Add fresh click handler
      newHeader.addEventListener('click', () => {
        // Toggle the active class on header
        newHeader.classList.toggle('active');
        
        // Toggle expanded class on content
        poiContent.classList.toggle('expanded');
      });
    } else {
      console.error('Could not find POI header or content');
    }
  }
  
  /**
   * Set up basemap control handlers
   */
  setupBasemapHandlers() {
    const basemapIds = ['osm-basemap', 'carto-basemap', 'esri-imagery', 'esri-streetmap', 'esri-topo', 'esri-relief'];
    
    basemapIds.forEach(id => {
      document.getElementById(id).addEventListener('change', (e) => {
        if (e.target.checked) {
          this.mapController.hideAllBasemaps();
          
          // Map the HTML ID to the correct config key
          let basemapKey;
          if (id === 'osm-basemap') {
            basemapKey = 'osm';
          } else if (id === 'carto-basemap') {
            basemapKey = 'carto';
          } else if (id === 'esri-imagery') {
            basemapKey = 'esriImagery';
          } else if (id === 'esri-streetmap') {
            basemapKey = 'esriStreetMap';
          } else if (id === 'esri-topo') {
            basemapKey = 'esriTopo';
          } else if (id === 'esri-relief') {
            basemapKey = 'esriRelief';
          }
          
          if (basemapKey && this.mapController.basemapLayers[basemapKey]) {
            this.mapController.basemapLayers[basemapKey].setVisible(true);
          } else {
            console.error(`Basemap layer not found for key: ${basemapKey}`);
          }
        }
      });
    });
  }
  
  /**
   * Set up layer type control handlers
   */
  setupLayerTypeHandlers() {
    document.getElementById('boundary-toggle').addEventListener('change', () => {
      this.mapController.updateAllLayersOpacity();
    });
    
    document.getElementById('points-toggle').addEventListener('change', () => {
      this.mapController.updateAllLayersOpacity();
    });

    // Handle reference layer radio buttons in a more maintainable way
    const referenceRadios = document.querySelectorAll('input[name="reference-layer"]');
    referenceRadios.forEach(radio => {
      radio.addEventListener('change', (e) => {
        if (e.target.checked) {
          // Find which reference layer key this radio button corresponds to
          const layerKey = Object.keys(CONFIG.referenceLayers).find(key => 
            CONFIG.referenceLayers[key].id === e.target.id
          );
          
          if (layerKey) {
            // Use the centralized method to update visibility
            this.mapController.setReferenceLayerVisibility(layerKey);
          } else {
            console.error(`No reference layer found for ID: ${e.target.id}`);
          }
        }
      });
    });
  }
  
  /**
   * Set up sigma toggle control handlers
   */
  setupSigmaToggleHandlers() {
    CONFIG.sigmaValues.forEach(sigma => {
      const toggle = document.getElementById(`sigma-${sigma}-toggle`);
      if (toggle) {
        toggle.addEventListener('change', (e) => this.handleSigmaToggle(sigma, e));
      }
      
      const slider = document.getElementById(`opacity-${sigma}`);
      if (slider) {
        slider.addEventListener('input', (e) => this.handleOpacitySlider(sigma, e));
      }
    });
  }
  
  /**
   * Set up POI slider handlers
   */
  setupPoiHandler() {
    const linearSlider = document.getElementById('poi-slider');
    const logSlider = document.getElementById('poi-slider-log');
    const linearScale = document.getElementById('linear-scale');
    const logScale = document.getElementById('log-scale');
    
    // Standard logarithmic scale conversion functions - make them available on the instance
    this.linearToLog = (value) => {
      const linearValue = parseInt(value, 10);
      if (linearValue === 0) return 0;
      return Math.log10(linearValue + 1);
    };
    
    this.logToLinear = (value) => {
      const logValue = parseFloat(value);
      if (logValue === 0) return 0;
      return Math.round(Math.pow(10, logValue) - 1);
    };
    
    // Event listeners for linear slider
    linearSlider.addEventListener('input', () => {
      // Update the logarithmic slider to match
      logSlider.value = this.linearToLog(linearSlider.value);
      
      // Only update filters if linear scale is selected
      if (linearScale.checked) {
        this.updatePoiValue();
      }
    });
    
    // Event listeners for logarithmic slider
    logSlider.addEventListener('input', () => {
      // Update the linear slider to match
      const linearValue = this.logToLinear(logSlider.value);
      linearSlider.value = linearValue;
      
      // Only update filters if log scale is selected
      if (logScale.checked) {
        this.updatePoiValue();
      }
    });
    
    // Toggle between linear and logarithmic scales
    linearScale.addEventListener('change', () => {
      if (linearScale.checked) {
        linearSlider.style.display = 'block';
        logSlider.style.display = 'none';
        
        // Update the App State
        AppState.poiScale = 'linear';
      }
    });
    
    logScale.addEventListener('change', () => {
      if (logScale.checked) {
        linearSlider.style.display = 'none';
        logSlider.style.display = 'block';
        
        // When switching to log scale, ensure the log slider has a proper value
        logSlider.value = this.linearToLog(linearSlider.value);
        
        // Update the App State
        AppState.poiScale = 'logarithmic';
      }
    });
    
    // Initialize with logarithmic slider
    linearSlider.style.display = 'none';
    logSlider.style.display = 'block';
    
    // Make sure log scale is checked (should already be from HTML)
    logScale.checked = true;
    linearScale.checked = false;
    
    // Set initial App State
    AppState.poiScale = 'logarithmic';
    
    // Initial update to set the filters
    this.updatePoiValue();
  }
  
  /**
   * Update POI value display and filters
   */
  updatePoiValue() {
    const linearSlider = document.getElementById('poi-slider');
    const logSlider = document.getElementById('poi-slider-log');
    const linearScale = document.getElementById('linear-scale');
    const logScale = document.getElementById('log-scale');
    const poiValue = document.getElementById('poi-value');
    
    let minPois;
    
    // Determine which slider is active and use its value
    if (logScale.checked) {
      // When log scale is active, convert the log slider value to linear
      const logValue = parseFloat(logSlider.value);
      
      // Standard logarithmic conversion
      if (logValue === 0) {
        minPois = 0;
      } else {
        minPois = Math.round(Math.pow(10, logValue) - 1);
      }
    } else {
      // When linear scale is active, use linear slider value directly
      minPois = parseInt(linearSlider.value, 10);
    }
    
    // Update the value display in the header
    poiValue.textContent = minPois.toString();
    
    // Apply filters
    this.mapController.updatePointsFilter(minPois);
    this.mapController.updateBoundaryFilter(minPois);
  }
  
  /**
   * Set up time slider control handlers
   */
  setupTimeSliderHandlers() {
    const sigmaSlider = document.getElementById('sigma-slider');
    const sigmaDisplay = document.getElementById('sigma-display');
    const playPauseBtn = document.getElementById('play-pause-btn');
    const playbackSpeedSelect = document.getElementById('playback-speed'); // Get the speed select element

    // Add keyboard navigation animation state
    let keyboardAnimationId = null;
    let keyboardAnimationStartTime = null;
    let keyboardAnimationStartSigma = null;
    let keyboardAnimationTargetSigma = null;
    const KEYBOARD_ANIMATION_DURATION = 500; // Duration in milliseconds
    
    // Use arrow function to preserve the 'this' context
    const animateToSigma = (timestamp) => {
      if (!keyboardAnimationStartTime) {
        keyboardAnimationStartTime = timestamp;
      }
      
      const elapsed = timestamp - keyboardAnimationStartTime;
      const progress = Math.min(elapsed / KEYBOARD_ANIMATION_DURATION, 1);
      
      // Ease in-out function
      const easeProgress = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;
      
      // Calculate current sigma
      const currentSigma = keyboardAnimationStartSigma + (keyboardAnimationTargetSigma - keyboardAnimationStartSigma) * easeProgress;
      
      // Update slider and display
      sigmaSlider.value = currentSigma;
      sigmaDisplay.textContent = Math.round(currentSigma);
      AppState.sigmaValue = currentSigma;
      this.updateLayersBySigma(currentSigma);
      
      // Continue animation if not complete
      if (progress < 1) {
        keyboardAnimationId = requestAnimationFrame(animateToSigma);
      } else {
        // Clean up animation state
        keyboardAnimationId = null;
        keyboardAnimationStartTime = null;
        keyboardAnimationStartSigma = null;
        keyboardAnimationTargetSigma = null;
      }
    };
    
    // Add keyboard navigation for the sigma slider
    sigmaSlider.addEventListener('keydown', (e) => {
      // Only handle left/right arrow keys
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      
      // Prevent default slider behavior
      e.preventDefault();
      
      // If animation is already running, complete it immediately
      if (keyboardAnimationId) {
        cancelAnimationFrame(keyboardAnimationId);
        sigmaSlider.value = keyboardAnimationTargetSigma;
        AppState.sigmaValue = keyboardAnimationTargetSigma;
        this.updateLayersBySigma(keyboardAnimationTargetSigma);
      }
      
      // Get current and target sigma values
      const currentSigma = parseFloat(sigmaSlider.value);
      let targetSigma;
      
      if (e.key === 'ArrowLeft') {
        // Find the next available sigma that's less than the current one
        targetSigma = CONFIG.sigmaValues.reduce((prev, sigma) => {
          if (sigma < currentSigma && (!prev || sigma > prev)) {
            return sigma;
          }
          return prev;
        }, null);
        
        // If no previous sigma found, wrap to the highest sigma
        if (!targetSigma) {
          targetSigma = CONFIG.sigmaValues[CONFIG.sigmaValues.length - 1];
        }
      } else {
        // Find the next available sigma that's greater than the current one
        targetSigma = CONFIG.sigmaValues.find(sigma => sigma > currentSigma) || CONFIG.sigmaValues[0];
      }
      
      // Start the animation
      keyboardAnimationStartSigma = currentSigma;
      keyboardAnimationTargetSigma = targetSigma;
      keyboardAnimationStartTime = null;
      keyboardAnimationId = requestAnimationFrame(animateToSigma);
    });

    // Update sigma display and layer opacities when slider changes
    sigmaSlider.addEventListener('input', (e) => {
      AppState.sigmaValue = parseFloat(e.target.value);
      sigmaDisplay.textContent = Math.round(AppState.sigmaValue);
      this.updateLayersBySigma(AppState.sigmaValue);
    });
    
    // Make the slider snap to available data sigma values when user releases it
    sigmaSlider.addEventListener('mouseup', () => {
      // Only snap if we're not in animation mode
      if (!AppState.isPlaying) {
        const nearest = findNearestSigma(AppState.sigmaValue);
        
        // Snap to that sigma
        AppState.sigmaValue = nearest;
        sigmaSlider.value = AppState.sigmaValue;
        sigmaDisplay.textContent = AppState.sigmaValue;
        this.updateLayersBySigma(AppState.sigmaValue);
      }
    });
    
    // Also handle touch events for mobile
    sigmaSlider.addEventListener('touchend', () => {
      // Only snap if we're not in animation mode
      if (!AppState.isPlaying) {
        const nearest = findNearestSigma(AppState.sigmaValue);
        
        // Snap to that sigma
        AppState.sigmaValue = nearest;
        sigmaSlider.value = AppState.sigmaValue;
        sigmaDisplay.textContent = AppState.sigmaValue;
        this.updateLayersBySigma(AppState.sigmaValue);
      }
    });
    
    // Play/Pause button functionality
    playPauseBtn.addEventListener('click', () => {
      if (AppState.isPlaying) {
        this.stopSigmaAnimation();
        playPauseBtn.textContent = '\u25B6'; // Play symbol
      } else {
        this.startSigmaAnimation();
        playPauseBtn.textContent = '\u23F8'; // Pause symbol
      }
      AppState.isPlaying = !AppState.isPlaying;
    });

    // Add event listener for playback speed changes
    if (playbackSpeedSelect) {
      playbackSpeedSelect.addEventListener('change', (e) => {
        AppState.animationSpeed = parseFloat(e.target.value);
      });
      // Initialize AppState.animationSpeed with the current value on load
      AppState.animationSpeed = parseFloat(playbackSpeedSelect.value);
    }
  }
  
  /**
   * Handle sigma toggle checkbox changes
   */
  handleSigmaToggle(sigma, e) {
    const slider = document.getElementById(`opacity-${sigma}`);
    
    if (!e.target.checked) {
      // Get current opacity before disabling
      const currentOpacity = parseInt(slider.value, 10);
      
      // Save current value before disabling
      AppState.previousOpacities[sigma] = currentOpacity / 100;
      
      // Set opacity to 0 when disabled
      slider.value = 0;
      document.getElementById(`opacity-value-${sigma}`).textContent = '0%';
      
      // Disable the slider
      slider.disabled = true;
      
      // Only distribute opacity if there are other enabled layers
      const enabledSigmas = AppState.getEnabledSigmaValues();
      if (enabledSigmas.length > 0) {
        this.distributeOpacityProportionally(sigma, currentOpacity);
      }
    } else {
      // Enable the slider
      slider.disabled = false;
      
      // Calculate current sum of opacities from other enabled layers
      const enabledSigmas = AppState.getEnabledSigmaValues().filter(s => s !== sigma);
      let currentSum = 0;
      
      enabledSigmas.forEach(s => {
        currentSum += parseInt(document.getElementById(`opacity-${s}`).value, 10);
      });
      
      // Set new layer opacity to (1 - sum of existing)
      let newOpacity = 0;
      if (enabledSigmas.length === 0) {
        // If no other layers, set to 100%
        newOpacity = 100;
      } else {
        // Otherwise, set to remaining opacity (with minimum of 0)
        newOpacity = Math.max(0, 100 - currentSum);
      }
      
      slider.value = newOpacity;
      document.getElementById(`opacity-value-${sigma}`).textContent = `${newOpacity}%`;
      AppState.previousOpacities[sigma] = newOpacity / 100;
    }
    
    // Update layer opacities
    this.mapController.updateAllLayersOpacity();
  }
  
  /**
   * Handle opacity slider changes
   */
  handleOpacitySlider(sigma, e) {
    // Update the displayed opacity value
    document.getElementById(`opacity-value-${sigma}`).textContent = `${e.target.value}%`;
    AppState.lastAdjustedSlider = sigma;
    this.redistributeOpacity(sigma);
  }
  
  /**
   * Redistribute opacity among sliders
   */
  redistributeOpacity(changedSigma) {
    // Get current values from all sliders
    const currentOpacities = {};
    CONFIG.sigmaValues.forEach(sigma => {
      currentOpacities[sigma] = parseInt(document.getElementById(`opacity-${sigma}`).value, 10) / 100;
    });
    
    // Get checkbox status for each sigma
    const sigmaEnabled = {};
    CONFIG.sigmaValues.forEach(sigma => {
      sigmaEnabled[sigma] = document.getElementById(`year-${sigma}-toggle`).checked;
    });
    
    // Calculate change in opacity for the changed slider
    const opacityChange = currentOpacities[changedSigma] - AppState.previousOpacities[changedSigma];
    
    // If no change, do nothing
    if (opacityChange === 0) return;
    
    // Sum of other opacities before adjustment (only considering enabled sigmas)
    const otherSigmas = CONFIG.sigmaValues.filter(sigma => 
      sigma !== changedSigma && sigmaEnabled[sigma]
    );
    
    // If no other sigmas are enabled, don't redistribute opacity
    if (otherSigmas.length === 0) {
      // Update previous opacity values and layer opacities
      Object.assign(AppState.previousOpacities, currentOpacities);
      this.mapController.updateAllLayersOpacity();
      return;
    }
    
    const sumOtherOpacities = otherSigmas.reduce((sum, sigma) => sum + AppState.previousOpacities[sigma], 0);
    
    // Calculate new values for other sliders based on their proportional values
    if (sumOtherOpacities > 0) {
      // Distribute the change proportionally
      otherSigmas.forEach(sigma => {
        const proportion = AppState.previousOpacities[sigma] / sumOtherOpacities;
        const newValue = Math.max(0, Math.min(1, AppState.previousOpacities[sigma] - (opacityChange * proportion)));
        
        // Update the slider value and displayed text
        const sliderValue = Math.round(newValue * 100);
        document.getElementById(`opacity-${sigma}`).value = sliderValue;
        document.getElementById(`opacity-value-${sigma}`).textContent = `${sliderValue}%`;
        currentOpacities[sigma] = newValue;
      });
    } else {
      // If other layers were at 0, distribute equally
      const newIndividualOpacity = Math.max(0, (1 - currentOpacities[changedSigma]) / otherSigmas.length);
      otherSigmas.forEach(sigma => {
        const sliderValue = Math.round(newIndividualOpacity * 100);
        document.getElementById(`opacity-${sigma}`).value = sliderValue;
        document.getElementById(`opacity-value-${sigma}`).textContent = `${sliderValue}%`;
        currentOpacities[sigma] = newIndividualOpacity;
      });
    }
    
    // Update previous opacity values for next change
    Object.assign(AppState.previousOpacities, currentOpacities);
    
    // Update actual layer opacities
    this.mapController.updateAllLayersOpacity();
  }
  
  /**
   * Update population value display and filters
   */
  updatePopulationValue() {
    const linearSlider = document.getElementById('population-slider');
    const logSlider = document.getElementById('population-slider-log');
    const linearScale = document.getElementById('linear-scale');
    const logScale = document.getElementById('log-scale');
    const populationValue = document.getElementById('population-value');
    
    let minPop;
    
    // Determine which slider is active and use its value
    if (logScale.checked) {
      // When log scale is active, convert the log slider value to linear
      const logValue = parseFloat(logSlider.value);
      
      // Standard logarithmic conversion
      if (logValue === 0) {
        minPop = 0;
      } else {
        minPop = Math.round(Math.pow(10, logValue) - 1);
      }
    } else {
      // When linear scale is active, use linear slider value directly
      minPop = parseInt(linearSlider.value, 10);
    }
    
    // Update the value display in the header
    const formattedPop = formatPopulation(minPop);
    populationValue.textContent = formattedPop;
    
    // Apply filters
    this.mapController.updatePointsFilter(minPop);
    this.mapController.updateBoundaryFilter(minPop);
  }
  
  /**
   * Start sigma animation
   */
  startSigmaAnimation() {
    if (AppState.animationFrameId) return; // Already running
    
    AppState.lastTimestamp = performance.now();
    AppState.animationFrameId = requestAnimationFrame(this.animateSigma.bind(this));
  }
  
  /**
   * Stop sigma animation
   */
  stopSigmaAnimation() {
    if (AppState.animationFrameId) {
      cancelAnimationFrame(AppState.animationFrameId);
      AppState.animationFrameId = null;
    }
    
    // Reset pause state when stopping animation
    AppState.isPaused = false;
    
    // Snap to the active sigma value (the one with opacity > 0.5)
    const activeSigma = AppState.getActiveSigma();
    AppState.sigmaValue = activeSigma;
    const sigmaSlider = document.getElementById('year-slider');
    sigmaSlider.value = activeSigma;
    
    // Reset all layer opacities to 0
    CONFIG.sigmaValues.forEach(sigma => {
      const opacitySlider = document.getElementById(`opacity-${sigma}`);
      if (opacitySlider) {
        opacitySlider.value = sigma === activeSigma ? 100 : 0;
        const opacityValueDisplay = document.getElementById(`opacity-value-${sigma}`);
        if (opacityValueDisplay) {
          opacityValueDisplay.textContent = `${opacitySlider.value}%`;
        }
      }
    });
    
    // Update actual layer opacities
    this.mapController.updateAllLayersOpacity();
    
    // Make sure the display reflects the active sigma
    const sigmaDisplay = document.getElementById('year-display');
    sigmaDisplay.textContent = activeSigma;
  }
  
  /**
   * Animate sigma slider
   */
  animateSigma(timestamp) {
    const sigmaSlider = document.getElementById('sigma-slider');
    const sigmaDisplay = document.getElementById('sigma-display');
    const playbackSpeedSelect = document.getElementById('playback-speed');
    
    // Handle the pause at the end of each cycle
    if (AppState.isPaused) {
      // Check if we've been paused long enough
      if (timestamp - AppState.pauseStartTime >= CONFIG.animation.duration) {
        // Resume animation and reset to the minimum sigma
        AppState.isPaused = false;
        AppState.sigmaValue = parseFloat(sigmaSlider.min);
        sigmaSlider.value = AppState.sigmaValue;
        this.updateLayersBySigma(AppState.sigmaValue);
      }
      // Continue the animation loop but don't update while paused
      AppState.lastTimestamp = timestamp;
      AppState.animationFrameId = requestAnimationFrame(this.animateSigma.bind(this));
      return;
    }
    
    const elapsed = timestamp - AppState.lastTimestamp;
    const playbackSpeed = parseFloat(playbackSpeedSelect.value);
    
    // Speed factor: values per second based on config
    const valuesPerSecond = CONFIG.animation.sigmasPerSecond * playbackSpeed;
    
    // Calculate new sigma value
    AppState.sigmaValue += (elapsed / 1000) * valuesPerSecond;
    
    // If we reached the max, start pause before looping back
    if (AppState.sigmaValue > parseFloat(sigmaSlider.max)) {
      // Set to exactly max value to ensure we show the final sigma value correctly
      AppState.sigmaValue = parseFloat(sigmaSlider.max);
      sigmaSlider.value = AppState.sigmaValue;
      this.updateLayersBySigma(AppState.sigmaValue);
      
      // Start pause
      AppState.isPaused = true;
      AppState.pauseStartTime = timestamp;
      AppState.lastTimestamp = timestamp;
      AppState.animationFrameId = requestAnimationFrame(this.animateSigma.bind(this));
      return;
    }
    
    // Update the slider value
    sigmaSlider.value = AppState.sigmaValue;
    
    // Update the layer opacities
    this.updateLayersBySigma(AppState.sigmaValue);
    
    AppState.lastTimestamp = timestamp;
    AppState.animationFrameId = requestAnimationFrame(this.animateSigma.bind(this));
  }
  
  /**
   * Update layer opacities based on sigma value
   */
  updateLayersBySigma(sigma) {
    // Enable all sigma toggles for the time slider
    CONFIG.sigmaValues.forEach(s => {
      const toggle = document.getElementById(`year-${s}-toggle`);
      if (toggle) {
        toggle.checked = true;
      }
    });
    
    // Determine which two sigma values we're between
    let lowerIndex = 0;
    for (let i = 0; i < CONFIG.sigmaValues.length - 1; i++) {
      if (sigma >= CONFIG.sigmaValues[i] && sigma <= CONFIG.sigmaValues[i + 1]) {
        lowerIndex = i;
        break;
      }
    }
    
    const lowerSigma = CONFIG.sigmaValues[lowerIndex];
    const upperSigma = CONFIG.sigmaValues[lowerIndex + 1];
    
    // Calculate interpolation factor (0-1)
    const range = upperSigma - lowerSigma;
    const factor = range > 0 ? (sigma - lowerSigma) / range : 0;
    
    // Reset all layer opacities to 0
    CONFIG.sigmaValues.forEach(s => {
      const opacitySlider = document.getElementById(`opacity-${s}`);
      if (opacitySlider) {
        opacitySlider.value = 0;
      }
    });
    
    // Set opacities for the two relevant sigma values
    const lowerSigmaSlider = document.getElementById(`opacity-${lowerSigma}`);
    const upperSigmaSlider = document.getElementById(`opacity-${upperSigma}`);
    
    if (lowerSigmaSlider && upperSigmaSlider) {
      lowerSigmaSlider.value = Math.round(100 * (1 - factor));
      upperSigmaSlider.value = Math.round(100 * factor);
    }
    
    // Update opacity display values for all sigma values
    CONFIG.sigmaValues.forEach(s => {
      const opacitySlider = document.getElementById(`opacity-${s}`);
      const opacityValueDisplay = document.getElementById(`opacity-value-${s}`);
      if (opacitySlider && opacityValueDisplay) {
        opacityValueDisplay.textContent = `${opacitySlider.value}%`;
      }
    });
    
    // Update actual layer opacities
    this.mapController.updateAllLayersOpacity();
    
    // Update year display with the active sigma value
    const sigmaDisplay = document.getElementById('sigma-display');
    const activeSigma = AppState.getActiveSigma();
    if (sigmaDisplay) {
      sigmaDisplay.textContent = activeSigma;
    }
  }
  
  /**
   * Set up location search handlers
   */
  setupLocationSearchHandlers() {
    const searchInput = document.getElementById('location-search');
    const searchButton = document.getElementById('search-button');
    const searchResults = document.getElementById('search-results');
    
    // Search button click handler
    searchButton.addEventListener('click', () => {
      this.performSearch(searchInput.value);
    });
    
    // Enter key in search input
    searchInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        this.performSearch(searchInput.value);
      }
    });
    
    // Clear results when input changes
    searchInput.addEventListener('input', () => {
      searchResults.innerHTML = '';
      searchResults.style.display = 'none';
    });
  }
  
  /**
   * Perform location search
   * @param {string} query - Search query
   */
  performSearch(query) {
    if (!query || query.trim() === '') return;
    
    const searchResults = document.getElementById('search-results');
    searchResults.innerHTML = '<div class="search-result">Searching...</div>';
    searchResults.style.display = 'block';
    
    this.mapController.searchLocation(query)
      .then(results => {
        searchResults.innerHTML = '';
        
        if (results.length === 0) {
          searchResults.innerHTML = '<div class="search-result">No results found</div>';
          return;
        }
        
        // Limit to top 5 results
        const topResults = results.slice(0, 5);
        
        topResults.forEach(result => {
          const resultElement = document.createElement('div');
          resultElement.className = 'search-result';
          resultElement.innerHTML = `${result.display_name}`;
          
          resultElement.addEventListener('click', () => {
            this.mapController.zoomToLocation(result);
            searchResults.innerHTML = '';
            searchResults.style.display = 'none';
          });
          
          searchResults.appendChild(resultElement);
        });
        
        // If only one result, automatically zoom to it
        if (results.length === 1) {
          this.mapController.zoomToLocation(results[0]);
          setTimeout(() => {
            searchResults.innerHTML = '';
            searchResults.style.display = 'none';
          }, 1000);
        }
      })
      .catch(error => {
        console.error('Search error:', error);
        searchResults.innerHTML = '<div class="search-result">Error searching location</div>';
      });
  }
  
  /**
   * Toggle population panel visibility
   */
  togglePopulationPanel() {
    const header = document.getElementById('population-header');
    const content = document.getElementById('population-content');
    
    if (header && content) {
      header.classList.toggle('active');
      content.classList.toggle('expanded');
    }
  }
  
  /**
   * Set up cell size selector handler
   */
  setupCellSizeHandler() {
    const cellSize100Radio = document.getElementById('cell-size-100');
    const cellSize1000Radio = document.getElementById('cell-size-1000');
    const cellSizeDisplay = document.getElementById('cell-size-display');
    
    // Initialize with the current value from AppState
    if (AppState.cellSize === 100) {
      cellSize100Radio.checked = true;
    } else if (AppState.cellSize === 1000) {
      cellSize1000Radio.checked = true;
    }
    
    // Make sure the display shows the current cell size
    cellSizeDisplay.textContent = AppState.cellSize.toString();
    
    // Add change event handlers for radio buttons
    [cellSize100Radio, cellSize1000Radio].forEach(radio => {
      radio.addEventListener('change', (e) => {
        if (e.target.checked) {
          const newCellSize = parseInt(e.target.value, 10);
          
          // Update cell size in the map controller which will recreate the layers
          if (this.mapController && newCellSize !== AppState.cellSize) {
            // Update display first
            cellSizeDisplay.textContent = newCellSize.toString();
            
            // Show updating message in sigma display
            const sigmaDisplay = document.getElementById('sigma-display');
            const originalContent = sigmaDisplay.textContent;
            sigmaDisplay.textContent = 'Updating...';
            
            // Update cell size in map controller
            this.mapController.updateCellSize(newCellSize);
            
            // Update AppState
            AppState.cellSize = newCellSize;
            
            // Restore original content after a short delay
            setTimeout(() => {
              sigmaDisplay.textContent = originalContent;
            }, 1000);
          }
        }
      });
    });
  }
  
  /**
   * Set up preset selector handler
   */
  setupPresetHandler() {
    const presetRadios = document.querySelectorAll('input[name="preset"]');
    const cellSizeRadios = document.querySelectorAll('input[name="cell-size"]');
    const sigmaSlider = document.getElementById('sigma-slider');
    const presetSubcentres = document.getElementById('preset-subcentres'); // Added declaration
    const presetCities = document.getElementById('preset-cities'); // Added declaration

    // Function to update cellsize and sigma
    const updateParameters = (cellSize, sigma) => {
      // Update cell size display and radio selection
      document.getElementById('cell-size-display').textContent = cellSize.toString();
      
      if (cellSize === 100) {
        document.getElementById('cell-size-100').checked = true;
      } else if (cellSize === 1000) {
        document.getElementById('cell-size-1000').checked = true;
      }
      
      // Update cell size in map controller if needed
      if (this.mapController && cellSize !== AppState.cellSize) {
        this.mapController.updateCellSize(cellSize);
        AppState.cellSize = cellSize;
      }
      
      // Update sigma slider and display
      if (CONFIG.sigmaValues.includes(sigma)) {
        const sigmaSlider = document.getElementById('sigma-slider'); // Corrected ID
        if (sigmaSlider) {
          sigmaSlider.value = sigma;
        } else {
          console.error("Sigma slider (ID: sigma-slider) not found in updateParameters.");
        }
        
        const sigmaDisplay = document.getElementById('sigma-display'); // Corrected ID
        if (sigmaDisplay) {
          sigmaDisplay.textContent = sigma.toString();
        } else {
          console.error("Sigma display (ID: sigma-display) not found in updateParameters.");
        }
        
        // Update layer opacities based on the new sigma
        AppState.sigmaValue = sigma;
        this.updateLayersBySigma(sigma);
      }
    };
    
    // Add change handlers for preset radio buttons
    if (presetSubcentres) {
      presetSubcentres.addEventListener('change', (e) => {
        if (e.target.checked) {
          // Subcentres: cell size 100, sigma 5
          updateParameters(100, 5);
        }
      });
    }
    
    if (presetCities) {
      presetCities.addEventListener('change', (e) => {
        if (e.target.checked) {
          // Cities: cell size 1000, sigma 12
          updateParameters(1000, 12);
        }
      });
    }
    
    const updatePresetSelection = () => {
      const currentCellSize = AppState.cellSize;
      const currentSigma = AppState.getActiveSigma();

      // Check if current settings match any preset
      if (currentCellSize === 100 && currentSigma === 5) {
        presetSubcentres.checked = true;
      } else if (currentCellSize === 1000 && currentSigma === 12) {
        presetCities.checked = true;
      } else {
        // If no preset matches, uncheck both preset radios
        presetSubcentres.checked = false;
        presetCities.checked = false;
        // REMOVED: presetCustom.checked = true;
      }
    };

    // Listen for changes to cell size
    cellSizeRadios.forEach(radio => {
      radio.addEventListener('change', updatePresetSelection);
    });
    
    // Listen for changes to sigma
    if (sigmaSlider) {
      sigmaSlider.addEventListener('change', updatePresetSelection);
    } else {
      // Updated error message to be more accurate
      console.error("Sigma slider element with ID 'sigma-slider' not found in setupPresetHandler.");
    }

    // Initial preset selection based on current state
    updatePresetSelection();
  }
}

export default UIController;